import { useCallback, useState } from "react";
import TablePagination from "@mui/material/TablePagination";
import Text from "@commons/Typography/Text";
import TablePaginationActions from "@commons/Table/TableContent/TablePaginationActions";
import type { Location } from "@typings/entities/location";
import { config } from "@/config";
import { useGetLocations } from "@services/location/list";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TextInput from "@commons/Input/Text";
import Button from "@commons/Button";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { isEmpty } from "@utils/formatters";
import Heading from "@commons/Typography/Heading";
import { usePage } from "@hooks/usePage";
import { usePermissions } from "@hooks/usePermissions";

const getValue = (value?: string | number | null) =>
  !isEmpty(value) ? value : "—";

export default function TableViewLocation() {
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const navigate = useNavigate();
  const { openModal } = usePage();
  const { can } = usePermissions();

  const {
    error,
    isLoading,
    data: locations,
  } = useGetLocations({
    search: searchQuery,
    limit: rowsPerPage,
    page: page + 1,
  });

  const handleView = (location: Location) => {
    navigate(`/community/${location.id}`);
  };

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleSearch = () => {
    setSearchQuery(search);
    setPage(0);
  };

  if (error) {
    toast.error("Erro ao carregar as localizações");
  }

  const canCreateLocation = can("create", "locations");

  return (
    <>
      <div className="flex flex-col justify-between sm:flex-row sm:items-center gap-4 mt-2">
        <div className="flex gap-4 w-full sm:max-w-sm">
          <TextInput
            textSize="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Código"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button
            startIcon={<SearchIcon />}
            customVariant="primary"
            size="md"
            onClick={handleSearch}
          >
            <Text>Filtrar</Text>
          </Button>
        </div>
        {canCreateLocation && (
          <Button
            startIcon={<AddIcon />}
            customVariant="secondary"
            size="auto"
            onClick={() => openModal("newLocation")}
          >
            <Text>Registrar Localização</Text>
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <Text>Carregando...</Text>
        ) : (
          <>
            {locations && locations.items.length === 0 && (
              <div className="w-full flex items-center justify-center h-24">
                <Text>Nenhuma localização encontrada.</Text>
              </div>
            )}
            {locations?.items.map((loc) => (
              <div
                key={loc.id}
                onClick={() => handleView(loc)}
                className={`
                bg-white rounded-xl shadow p-4 flex flex-col gap-2 relative
                cursor-pointer transition
                hover:bg-stone-100 hover:shadow-lg
                hover:scale-101
                active:scale-[0.98]
              `}
              >
                <Heading size="sm">
                  Código da Localização: {getValue(loc.code)}
                </Heading>
                <Text>
                  <Text component={"span"} className="font-medium">
                    Responsáveis:
                  </Text>{" "}
                  {getValue(loc.responsible)}
                </Text>
                <Text>
                  <Text component={"span"} className="font-medium">
                    Localização:
                  </Text>{" "}
                  {getValue(loc.address)}
                </Text>
                <Text>
                  <Text component={"span"} className="font-medium">
                    Contato:
                  </Text>{" "}
                  {getValue(loc.email)}, {getValue(loc.phone)}
                </Text>
                <Text>
                  <Text component={"span"} className="font-medium">
                    Data de Registro:
                  </Text>{" "}
                  {new Date(loc.createdAt).toLocaleDateString()}
                </Text>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="w-full flex flex-col sm:flex-row justify-end gap-2">
        <TablePagination
          className="hide-pagination-select w-full"
          rowsPerPageOptions={config.table.pageSizeOptions}
          count={locations?.meta.totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
          slotProps={{
            select: {
              inputProps: {
                "aria-label": "linhas por página",
              },
              native: true,
            },
          }}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
          sx={{
            "@media (max-width: 768px)": {
              ".MuiTablePagination-selectLabel, .MuiTablePagination-select, .MuiTablePagination-selectIcon":
                {
                  display: "none",
                },
            },
          }}
        />
      </div>
    </>
  );
}
